import { api } from '../api';
import { initAdminShell, showError, hideError, escapeHtml, setButtonBusy } from './shell';
import { t } from '../i18n/i18n';
import { mountImagesPanel } from './power-station-images';

interface PowerStation {
  id?: number;
  powerStationId: string | null;
  latitude: number | null;
  longitude: number | null;
  province: string | null;
  municipality: string | null;
  city: string | null;
  street: string | null;
  houseNumber: string | null;
  postalCode: string | null;
  licenceNeeded: boolean | null;
  positioning: string | null;
  access: string | null;
  primaryFunction: string | null;
  organizationName: string | null;
  contactPerson: string | null;
  email: string | null;
  malfunctionContactNumber: string | null;
  backupMalfunctionContactNumber: string | null;
  notAvailableAt: string | null;
  serviceProviders: string | null;
  servicePartyIdCode: string | null;
  website: string | null;
  applicationForm: string | null;
  minimumRequiredUserLevel: string | null;
  tariff: number | null;
  expiryDate: string | null;
  capacityValue: string | null;
  connectorTypes: string[];
  status: string;
  visibility: string;
  source: string;
  organizationId: string | null;
}

interface OrganizationOption { id: string; name: string; }

interface FieldDef {
  name: keyof PowerStation;
  label: string;
  type?: 'text' | 'number' | 'date' | 'checkbox' | 'tags' | 'select' | 'chip-multiselect' | 'url';
  step?: string;
  min?: string | number;
  max?: string | number;
  required?: boolean;
  group: string;
  options?: ReadonlyArray<{ value: string; labelKey: string }>;
}

const STATUS_OPTIONS = [
  { value: 'available',      labelKey: 'admin.point_status.available' },
  { value: 'maintenance',    labelKey: 'admin.point_status.maintenance' },
  { value: 'out_of_service', labelKey: 'admin.point_status.out_of_service' },
] as const;

const VISIBILITY_OPTIONS = [
  { value: 'public',     labelKey: 'admin.map.visibility.public' },
  { value: 'admin_only', labelKey: 'admin.map.visibility.admin_only' },
  { value: 'draft',      labelKey: 'admin.map.visibility.draft' },
] as const;

const SOURCE_OPTIONS = [
  { value: 'manual',      labelKey: 'admin.map.source.manual' },
  { value: 'csv_upload',  labelKey: 'admin.map.source.csv' },
  { value: 'api_import',  labelKey: 'admin.map.source.api' },
  { value: 'admin_scout', labelKey: 'admin.map.source.scout' },
] as const;

const fields: FieldDef[] = [
  { name: 'powerStationId', label: 'Power Station ID', required: true, group: 'Identity' },
  { name: 'organizationName', label: 'Organization', group: 'Identity' },
  { name: 'serviceProviders', label: 'Service providers (operator)', group: 'Identity' },
  { name: 'servicePartyIdCode', label: 'ID-code service party', group: 'Identity' },

  { name: 'street', label: 'Street', group: 'Location' },
  { name: 'houseNumber', label: 'House number', group: 'Location' },
  { name: 'postalCode', label: 'Postal code', group: 'Location' },
  { name: 'city', label: 'City', group: 'Location' },
  { name: 'municipality', label: 'Municipality', group: 'Location' },
  { name: 'province', label: 'Province', group: 'Location' },
  { name: 'latitude', label: 'Latitude', type: 'number', step: 'any', min: -90, max: 90, group: 'Location' },
  { name: 'longitude', label: 'Longitude', type: 'number', step: 'any', min: -180, max: 180, group: 'Location' },

  { name: 'positioning', label: 'Positioning (location type)', group: 'Operational' },
  { name: 'access', label: 'Access', group: 'Operational' },
  { name: 'primaryFunction', label: 'Primary function', group: 'Operational' },
  { name: 'minimumRequiredUserLevel', label: 'Minimum required user level', group: 'Operational' },
  { name: 'licenceNeeded', label: 'Licence needed', type: 'checkbox', group: 'Operational' },
  { name: 'tariff', label: 'Tariff (€/kWh)', type: 'number', step: '0.01', min: 0, max: 1000, group: 'Operational' },
  { name: 'capacityValue', label: 'Capacity', group: 'Operational' },
  { name: 'connectorTypes', label: 'Connector types', type: 'chip-multiselect', group: 'Operational' },

  { name: 'contactPerson', label: 'Contact person', group: 'Contact' },
  { name: 'email', label: 'Email', group: 'Contact' },
  { name: 'malfunctionContactNumber', label: 'Malfunction contact number', group: 'Contact' },
  { name: 'backupMalfunctionContactNumber', label: 'Backup malfunction contact', group: 'Contact' },
  { name: 'website', label: 'Website', type: 'url', group: 'Contact' },
  { name: 'applicationForm', label: 'Application form (URL)', type: 'url', group: 'Contact' },

  { name: 'notAvailableAt', label: 'Not available at', group: 'Schedule' },
  { name: 'expiryDate', label: 'Expiry date', type: 'date', group: 'Schedule' },

  { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, group: 'Status' },

  { name: 'visibility', label: 'Visibility', type: 'select', options: VISIBILITY_OPTIONS, group: 'Visibility & source' },
  { name: 'source', label: 'Source', type: 'select', options: SOURCE_OPTIONS, group: 'Visibility & source' },
];

const empty: PowerStation = {
  powerStationId: '', latitude: null, longitude: null, province: '', municipality: '', city: '',
  street: '', houseNumber: '', postalCode: '', licenceNeeded: null, positioning: '', access: '',
  primaryFunction: '', organizationName: '', contactPerson: '', email: '', malfunctionContactNumber: '',
  backupMalfunctionContactNumber: '', notAvailableAt: '', serviceProviders: '', servicePartyIdCode: '', website: '',
  applicationForm: '', minimumRequiredUserLevel: '', tariff: null, expiryDate: null,
  capacityValue: '', connectorTypes: [], status: 'available',
  visibility: 'public', source: 'manual', organizationId: null,
};

(async () => {
  let me;
  try {
    me = await initAdminShell('power-stations');
  } catch {
    return;
  }
  const isAdmin = me?.role === 'platform_admin';

  const id = new URL(window.location.href).searchParams.get('id');
  const editing = id !== null;
  const form = document.getElementById('ps-form') as HTMLFormElement | null;
  if (!form) return;

  // Organization picker is only meaningful for platform admin (organization
  // users get their own org forced server-side regardless of what the form
  // sends). Loading the list for organization users would 403 anyway since
  // /api/admin/organizations is admin-only.
  let organizations: OrganizationOption[] = [];
  if (isAdmin) {
    try {
      const client = await api();
      const { data } = await client.get<OrganizationOption[]>('/api/admin/organizations');
      organizations = data;
    } catch {
      // Picker stays empty; admin can still leave OrganizationId blank.
    }
  }

  // Lookup data for the Connector-types chip picker. OrganizationOrAbove so
  // it works for org users too. Empty array is fine - the picker just
  // renders with the legacy chips of whatever the row already has.
  let connectorOptions: string[] = [];
  try {
    const client = await api();
    const { data } = await client.get<string[]>('/api/admin/lookups/connector-types');
    connectorOptions = data;
  } catch {
    // Non-fatal - the picker still works with the row's existing chips.
  }

  let initial: PowerStation = empty;
  if (editing) {
    try {
      const client = await api();
      const { data } = await client.get<PowerStation>(`/api/admin/power-stations/${encodeURIComponent(id!)}`);
      initial = data;
    } catch (err) {
      showError('form-error', err instanceof Error ? err.message : t('admin.power_station_form.err_load'));
      return;
    }
  }

  // Render fields grouped, with values prefilled from `initial`.
  const groups = Array.from(new Set(fields.map((f) => f.group)));
  const ownerFieldset = isAdmin ? renderOrganizationFieldset(initial, organizations) : '';
  const submitLabel = editing ? t('admin.power_station_form.submit_edit') : t('admin.power_station_form.submit_new');
  form.innerHTML = ownerFieldset + groups.map((g) => `
    <fieldset class="form-group">
      <legend>${g}</legend>
      ${fields.filter((f) => f.group === g).map((f) => renderField(f, initial, { connectorOptions })).join('')}
    </fieldset>
  `).join('') + `
    <div class="form-actions">
      <button type="submit" class="btn btn--primary">${escapeHtml(submitLabel)}</button>
      <a href="/admin-power-stations.html" class="btn-link">${escapeHtml(t('admin.actions.cancel'))}</a>
    </div>
  `;
  form.hidden = false;

  // Wire the chip-multiselect pickers: click to toggle, "+ Add" button to
  // create a new chip when the desired connector type isn't in the list yet.
  form.querySelectorAll<HTMLDivElement>('.chip-picker').forEach((picker) => {
    const chipsHost = picker.querySelector<HTMLDivElement>('.chip-picker__chips');
    const addInput  = picker.querySelector<HTMLInputElement>('.chip-picker__input');
    const addBtn    = picker.querySelector<HTMLButtonElement>('.chip-picker__add-btn');
    if (!chipsHost) return;

    chipsHost.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const chip = target.closest<HTMLButtonElement>('button.filter-chip[data-chip-value]');
      if (chip) chip.classList.toggle('is-active');
    });

    function addNewChip(): void {
      const raw = addInput?.value.trim() ?? '';
      if (!raw) return;
      const existing = chipsHost!.querySelector<HTMLButtonElement>(`button.filter-chip[data-chip-value="${cssEscape(raw)}"]`);
      if (existing) {
        existing.classList.add('is-active');
      } else {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'filter-chip is-active';
        btn.dataset.chipValue = raw;
        btn.textContent = raw;
        chipsHost!.appendChild(btn);
      }
      if (addInput) addInput.value = '';
    }

    addBtn?.addEventListener('click', addNewChip);
    addInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); addNewChip(); }
    });
  });

  // Images section. On edit the full gallery (list / reorder / delete) mounts
  // against the existing row id. On create the row doesn't exist yet - we
  // render a multi-file picker that the submit handler will drain into the
  // upload endpoint after the station POST returns the new id.
  const actionsRow = form.querySelector('.form-actions');
  if (editing && initial.id != null) {
    const panel = document.createElement('div');
    panel.id = 'ps-images-panel';
    actionsRow?.insertAdjacentElement('beforebegin', panel);
    mountImagesPanel(panel, initial.id).catch(() => { /* panel surfaces its own errors */ });
  } else {
    const picker = document.createElement('fieldset');
    picker.className = 'form-group';
    picker.innerHTML = `
      <legend>${escapeHtml(t('admin.power_station_form.images_placeholder_title'))}</legend>
      <label class="form-field" for="ps-new-images">
        <span>${escapeHtml(t('admin.power_station_form.images_pick_label'))}</span>
        <input id="ps-new-images" name="newImages" type="file" accept="image/jpeg,image/png,image/webp" multiple/>
      </label>
      <p class="form-help">${escapeHtml(t('admin.power_station_form.images_pick_hint'))}</p>
    `;
    actionsRow?.insertAdjacentElement('beforebegin', picker);
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideError('form-error');
    const successBox = document.getElementById('form-success');
    if (successBox) successBox.hidden = true;

    const payload: Record<string, unknown> = {};
    for (const f of fields) {
      // chip-multiselect doesn't have a single form element; read the
      // picker's active chips out of the DOM directly.
      if (f.type === 'chip-multiselect') {
        const picker = form.querySelector<HTMLDivElement>(`.chip-picker[data-name="${cssEscape(String(f.name))}"]`);
        const values: string[] = [];
        picker?.querySelectorAll<HTMLButtonElement>('button.filter-chip.is-active[data-chip-value]')
          .forEach((b) => { const v = b.dataset.chipValue?.trim(); if (v) values.push(v); });
        payload[f.name as string] = values;
        continue;
      }
      const el = form.elements.namedItem(f.name as string) as HTMLInputElement | null;
      if (!el) continue;
      switch (f.type) {
        case 'checkbox':
          payload[f.name as string] = el.checked;
          break;
        case 'number':
          payload[f.name as string] = el.value === '' ? null : Number(el.value);
          break;
        case 'date':
          payload[f.name as string] = el.value === '' ? null : new Date(el.value).toISOString();
          break;
        case 'tags':
          payload[f.name as string] = el.value
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          break;
        default:
          payload[f.name as string] = el.value === '' ? null : el.value;
      }
    }
    // Organization: admin sends what the picker says; organization endpoint
    // ignores whatever we send and forces the user's own organization.
    if (isAdmin) {
      const orgSel = form.elements.namedItem('organizationId') as HTMLSelectElement | null;
      payload.organizationId = orgSel && orgSel.value !== '' ? orgSel.value : null;
    }

    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    setButtonBusy(submitBtn, true);
    try {
      const client = await api();
      if (editing) {
        await client.put(`/api/admin/power-stations/${encodeURIComponent(id!)}`, payload);
        if (successBox) {
          successBox.textContent = t('admin.power_station_form.saved');
          successBox.hidden = false;
          successBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        const { data: created } = await client.post<PowerStation>('/api/admin/power-stations', payload);
        const newId = created.id;
        const picker = form.elements.namedItem('newImages') as HTMLInputElement | null;
        const files = picker?.files ?? null;

        if (newId != null && files && files.length > 0) {
          let uploadFailed = false;
          for (let i = 0; i < files.length; i++) {
            if (successBox) {
              successBox.textContent = t('admin.power_station_form.uploading_progress')
                .replace('{i}', String(i + 1))
                .replace('{n}', String(files.length));
              successBox.hidden = false;
              if (i === 0) successBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            try {
              const fd = new FormData();
              fd.append('file', files[i]);
              await client.post(`/api/admin/power-stations/${newId}/images`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
            } catch (uploadErr) {
              const detail = extractServerMessage(uploadErr);
              const partial = t('admin.power_station_form.upload_partial');
              showError('form-error', detail ? `${partial} ${detail}` : partial);
              if (successBox) successBox.hidden = true;
              document.getElementById('form-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              uploadFailed = true;
              break;
            }
          }
          if (uploadFailed) return;
        }

        window.location.href = '/admin-power-stations.html';
        return;
      }
    } catch (err) {
      showError('form-error', extractServerMessage(err) ?? t('admin.power_station_form.err_save'));
      document.getElementById('form-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } finally {
      setButtonBusy(submitBtn, false);
    }
  });
})();

function extractServerMessage(err: unknown): string | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: unknown; status?: number } }).response;
    const data = resp?.data;
    if (typeof data === 'string' && data.length > 0) return data;
    if (data && typeof data === 'object') {
      // ASP.NET ProblemDetails validation responses (400) carry the actual
      // field-level errors under `errors`: { fieldName: [msg, ...] }. Flatten
      // those into a readable string so the admin sees what's actually wrong
      // instead of the generic "One or more validation errors occurred." title.
      const errors = (data as { errors?: unknown }).errors;
      if (errors && typeof errors === 'object') {
        const parts: string[] = [];
        for (const [field, msgs] of Object.entries(errors as Record<string, unknown>)) {
          const list = Array.isArray(msgs) ? msgs : [msgs];
          for (const m of list) {
            if (typeof m === 'string' && m.length > 0) {
              parts.push(field ? `${field}: ${m}` : m);
            }
          }
        }
        if (parts.length > 0) return parts.join(' · ');
      }
      const msg = (data as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.length > 0) return msg;
      const title = (data as { title?: unknown }).title;
      if (typeof title === 'string' && title.length > 0) return title;
    }
    if (resp?.status === 503) return 'Image storage is not configured. Contact a platform admin.';
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
}

function renderOrganizationFieldset(initial: PowerStation, organizations: OrganizationOption[]): string {
  const opts = `<option value="">${escapeHtml(t('admin.organization.unassigned'))}</option>` + organizations
    .map((o) => `<option value="${escapeHtml(o.id)}"${o.id === (initial.organizationId ?? '') ? ' selected' : ''}>${escapeHtml(o.name)}</option>`)
    .join('');
  return `
    <fieldset class="form-group">
      <legend>${escapeHtml(t('admin.organization.label'))}</legend>
      <label class="form-field" for="ps-organizationId">
        <span>${escapeHtml(t('admin.organization.label'))}</span>
        <select id="ps-organizationId" name="organizationId">${opts}</select>
      </label>
    </fieldset>`;
}

interface RenderCtx {
  connectorOptions: string[];
}

/** Minimal CSS.escape polyfill - only handles what data-chip-value can contain (letters, digits, spaces, slashes, dashes, plus signs). */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, (m) => `\\${m}`);
}

function renderField(f: FieldDef, initial: PowerStation, ctx: RenderCtx): string {
  const raw = initial[f.name];
  const inputId = `ps-${String(f.name)}`;
  if (f.type === 'checkbox') {
    const checked = raw === true ? 'checked' : '';
    return `
      <label class="form-field form-field--check" for="${inputId}">
        <input type="checkbox" id="${inputId}" name="${String(f.name)}" ${checked} />
        <span>${escapeHtml(f.label)}</span>
      </label>`;
  }
  if (f.type === 'tags') {
    const value = Array.isArray(raw) ? raw.join(', ') : '';
    return `
      <label class="form-field" for="${inputId}">
        <span>${escapeHtml(f.label)}</span>
        <input type="text" id="${inputId}" name="${String(f.name)}" value="${escapeHtml(value)}" />
      </label>`;
  }
  if (f.type === 'chip-multiselect') {
    // Each known connector type renders as a toggleable chip; chips for
    // values that are already on the row but absent from the lookup table
    // (legacy data) get appended so we never silently lose them.
    const current = Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
    const currentSet = new Set(current);
    const allValues = [...ctx.connectorOptions];
    for (const v of current) if (!allValues.includes(v)) allValues.push(v);
    const chips = allValues
      .map((v) => `<button type="button" class="filter-chip${currentSet.has(v) ? ' is-active' : ''}" data-chip-value="${escapeHtml(v)}">${escapeHtml(v)}</button>`)
      .join('');
    const addPh = t('admin.station_form.connector_add_ph');
    const addBtnLabel = t('admin.station_form.connector_add_btn');
    return `
      <label class="form-field">
        <span>${escapeHtml(f.label)}</span>
        <div class="chip-picker" data-name="${String(f.name)}">
          <div class="chip-picker__chips filter-chips">${chips}</div>
          <div class="chip-picker__add">
            <input type="text" class="chip-picker__input" maxlength="64" placeholder="${escapeHtml(addPh)}"/>
            <button type="button" class="btn btn--ghost chip-picker__add-btn">${escapeHtml(addBtnLabel)}</button>
          </div>
        </div>
      </label>`;
  }
  if (f.type === 'select') {
    const current = typeof raw === 'string' ? raw : '';
    const opts = (f.options ?? [])
      .map((o) => `<option value="${escapeHtml(o.value)}"${o.value === current ? ' selected' : ''}>${escapeHtml(t(o.labelKey))}</option>`)
      .join('');
    return `
      <label class="form-field" for="${inputId}">
        <span>${escapeHtml(f.label)}</span>
        <select id="${inputId}" name="${String(f.name)}">${opts}</select>
      </label>`;
  }
  let value = '';
  if (raw !== null && raw !== undefined) {
    if (f.type === 'date' && typeof raw === 'string') value = raw.slice(0, 10);
    else value = String(raw);
  }
  const step = f.step ? ` step="${f.step}"` : '';
  const min = f.min !== undefined ? ` min="${f.min}"` : '';
  const max = f.max !== undefined ? ` max="${f.max}"` : '';
  const required = f.required ? ' required' : '';
  return `
    <label class="form-field" for="${inputId}">
      <span>${escapeHtml(f.label)}</span>
      <input type="${f.type ?? 'text'}"${step}${min}${max}${required} id="${inputId}" name="${String(f.name)}" value="${escapeHtml(value)}" />
    </label>`;
}
